import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/api.service';
import { ShopTheming, ThemingService } from 'src/app/theming.service';
import { SettingsService } from 'src/app/settings.service';
import { LambdaService } from 'src/app/lambda.service';
import { TimekitTabsComponent } from '../timekit-tabs/timekit-tabs.component';
import { TimekitOptionsComponent } from '../timekit-options/timekit-options.component';

declare var $: any;

export interface Project {
  projectId: string;
  projectName: string;
  timekitResourceId: number;
  projectType: string | null;
  categoryId: number;
  subCategoryId: number;
  subSubCategoryId: number;
  externalId: string;
}

@Component({
  selector: 'app-timekit-create-project',
  templateUrl: './timekit-create-project.component.html',
  styleUrls: ['./timekit-create-project.component.scss']
})
export class TimekitCreateProjectComponent implements OnInit {
  projectForm: FormGroup;
  resources: any[] = [];
  loading: boolean = false;
  daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  daysOfWeekInSpanish: string[] = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  hours = Array.from({ length: 24 }, (_, i) => i);
  availability_constraints: any[] = [];
  public shopTheme: ShopTheming[] = [];
  resourceName!: string;
  showNoResourcesMessage: boolean = false;
  timekitResourceId: number = 0;
  months = Array.from({ length: 6 }, (_, i) => i + 1);
  minutes = [0, 10, 15, 30, 45];
  minutesOptions: number[] = [0, 15, 30, 45, 75, 90];
  selectedOption: string = '';
  categories: any[] = [];
  subCategories: any[] = [];
  subSubCategories: any[] = [];
  selectedCategoryId!: number
  selectedSubCategoryId!: number
  selectedSubSubCategoryId!: number
  projectIdToEdit: string = '';
  private _data: string | null = null;
  projectId: string = '';
  isEditMode: boolean = false;
  bufferOptions: number[] = [0, 10, 15, 30, 45];
  toControl = this.fb.control(1);
  fromRange: number[] = Array.from({ length: 30 }, (_, i) => i + 1);
  @Input() set data(value: string | null) {
    this._data = value;
    if (value) {
      this.projectId = value;
      this.isEditMode = true;
      this.loadProjectData(value);
    }
  }
  shopName!: string;
  projectType: string = '';
  projects: Project[] = [];
  showNoProjectsMessage: boolean = false;
  dbProjectLoaded: boolean = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    public themingService: ThemingService,
    private settingsService: SettingsService,
    private lambdaService: LambdaService
  ) {
    this.projectForm = this.fb.group({
      projectId: [''],
      name: ['', Validators.required],
      slug: ['', Validators.required],
      allow_conference: ['1', Validators.required],
      resource: ['', Validators.required],
      availability: this.fb.group({
        mode: ['roundrobin_random', Validators.required],
        length: this.fb.group({
          hours: [0, Validators.required],
          minutes: [0, Validators.required]
        }),
        from: this.fb.group({
          value: [1, Validators.required],
          unit: ['days', Validators.required]
        }),
        availability: this.fb.group({
          to: [1, Validators.required],
        }),
        buffer: this.fb.group({
          value: [0, Validators.required],
          unit: ['minutes', Validators.required]
        }),
        ignore_all_day_events: [false, Validators.required]
      }),
      availability_constraints: this.fb.array([]),
      block_hours: this.fb.array([]),
      booking: this.fb.group({
        graph: ['instant', Validators.required],
        what: ['', Validators.required],
        where: ['', Validators.required],
        description: ['', Validators.required]
      }),
      customer_fields: this.fb.array([]),
      projectType: ['private', Validators.required],
      categoryOption: [''],
      selectedCategory: [''],
      selectedSubCategory: [''],
      selectedSubSubCategory: [''],
    });
    this.addAvailabilityConstraint();
  }

  ngOnInit(): void {
    this.shopName = this.themingService.shopTheming[0]?.shopName;
    this.getTimekitResources()
    this.loadCategories();
    this.loadSubCategories();
    this.loadSubSubCategories();
    this.isEditMode = !!this.projectId;

    if (this.isEditMode) {
      this.projectId = this.projectId || this.projectForm.get('projectId')?.value;
      this.loadProjectWithExtras(this.projectId);
    }

    if (!this.isEditMode) {
      const slug = this.generateSlug();
      this.projectForm.get('slug')?.setValue(slug);
    }

    this.lengthGroup.valueChanges.subscribe(({ hours, minutes }) => {
      const totalMinutes = hours * 60 + minutes;
    });

    this.getBufferGroup().get('unit')?.valueChanges.subscribe(unit => {
      if (unit === 'minutes') {
        this.bufferOptions = [0, 10, 15, 30, 45];
      } else if (unit === 'hours') {
        this.bufferOptions = Array.from({ length: 24 }, (_, i) => i + 1);
      }
      this.getBufferGroup().get('value')?.setValue(this.bufferOptions[0]);
    });

    this.projectForm.get('availability.from.unit')?.valueChanges.subscribe(unit => {
      this.fromRange =
        unit === 'days'
          ? Array.from({ length: 30 }, (_, i) => i + 1)
          : Array.from({ length: 24 }, (_, i) => i);

      this.projectForm.get('availability.from.value')?.setValue(this.fromRange[0]);
    });

    this.projectForm.get('categoryOption')?.valueChanges.subscribe(option => {
      const cat = this.projectForm.get('selectedCategory');
      const sub = this.projectForm.get('selectedSubCategory');
      const subsub = this.projectForm.get('selectedSubSubCategory');

      cat?.clearValidators();
      sub?.clearValidators();
      subsub?.clearValidators();

      if (this.projectForm.get('projectType')?.value === 'private') {
        switch (option) {
          case 'categories':
            cat?.setValidators([Validators.required]);
            break;
          case 'subCategories':
            sub?.setValidators([Validators.required]);
            break;
          case 'subSubcategories':
            subsub?.setValidators([Validators.required]);
            break;
        }
      }
      [cat, sub, subsub].forEach(ctrl => ctrl?.updateValueAndValidity());
    });
    this.projectForm.get('selectedCategory')?.valueChanges.subscribe(val => this.selectedCategoryId = +val);
    this.projectForm.get('selectedSubCategory')?.valueChanges.subscribe(val => this.selectedSubCategoryId = +val);
    this.projectForm.get('selectedSubSubCategory')?.valueChanges.subscribe(val => this.selectedSubSubCategoryId = +val);
  }

  private getSelectedOptionFromProject(project: Project): string {
    if (project.subSubCategoryId) return 'subSubcategories';
    if (project.subCategoryId) return 'subCategories';
    if (project.categoryId) return 'categories';
    return '';
  }

  backToListing = () => {
    this.settingsService.setListingProjects();

    const tabsComponent = (window as any).timekitTabs;
    if (tabsComponent?.setActiveTab) {
      const index = tabsComponent['tabs'].findIndex((t: { component: typeof TimekitOptionsComponent; }) => t.component === TimekitOptionsComponent);
      tabsComponent.setActiveTab(index);
    } else {
      console.error('TimekitTabsComponent no está disponible');
    }
  }

  ngAfterViewInit() {
    if (this.isEditMode) {}
  }

  get lengthGroup(): FormGroup {
    return this.projectForm.get('availability.length') as FormGroup;
  }

  get bufferGroup(): FormGroup {
    return this.projectForm.get('availability.buffer') as FormGroup;
  }

  pad(n: unknown): string {
    const value = typeof n === 'number' ? n : parseInt(n as string, 10);
    return value.toString().padStart(2, '0');
  }

  addCustomerField(): void {
    const customerFieldsArray = this.projectForm.get('customer_fields') as FormArray;
    customerFieldsArray.push(this.fb.group({
      title: ['', Validators.required],
      prefilled: [''],
      required: [false],
      readonly: [false]
    }));
  }

  getBufferGroup(): FormGroup {
    return this.projectForm.get('availability.buffer') as FormGroup;
  }

  sanitizeAvailability(raw: any): any {
    return {
      mode: raw?.mode || "roundrobin_random",
      from: raw?.from || "2 hours",
      to: raw?.to || "4 weeks",
      length: raw?.length || "2 hours",
      buffer: raw?.buffer || "10 minutes",
      ignore_all_day_events: raw?.ignore_all_day_events !== undefined
        ? raw.ignore_all_day_events
        : true
    };
  }

  setAvailabilityConstraints(data: any[]) {
    const constraints = data
      .filter(c => c.allow_day_and_time)
      .map(c => this.fb.group({
        allow_day_and_time: this.fb.group({
          day: c.allow_day_and_time.day,
          start: c.allow_day_and_time.start,
          end: c.allow_day_and_time.end,
        }),
      }));

    const formArray = this.fb.array(constraints);
    this.projectForm.setControl('availability_constraints', formArray);
  }

  setBlockHours(data: any[]) {
    const blocks = data
      .filter(b => b.block_hours)
      .map(b => this.fb.group({
        start: b.block_hours.start,
        end: b.block_hours.end,
      }));

    const formArray = this.fb.array(blocks);
    this.projectForm.setControl('block_hours', formArray);
  }

  setCustomerFields(data: any) {
    const fields = Object.values(data).map((field: any) =>
      this.fb.group({
        title: [field.title || ''],
        required: [field.required || false],
        prefilled: [field.prefilled || ''],
        readonly: [field.readonly || false]
      })
    );

    const formArray = this.fb.array(fields);
    this.projectForm.setControl('customer_fields', formArray);
  }

  private formatAvailabilityConstraints(ac: any[], block: any[]): any[] {
    const constraints: any[] = [];

    for (const c of ac) {
      if (c.allow_day_and_time?.day && c.allow_day_and_time?.start && c.allow_day_and_time?.end) {
        constraints.push({
          allow_day_and_time: {
            day: c.allow_day_and_time.day,
            start: c.allow_day_and_time.start,
            end: c.allow_day_and_time.end
          }
        });
      }
    }

    for (const b of block) {
      if (b.start && b.end) {
        constraints.push({
          block_hours: {
            start: b.start,
            end: b.end
          }
        });
      }
    }

    return constraints;
  }

  private formatCustomerFields(fields: any[]): any {
    return fields.reduce((acc: any, field: any) => {
      acc[field.title.toLowerCase()] = field;
      return acc;
    }, {});
  }

  private parseTimeStringToObject(str: string): { hours: number, minutes: number } {
    const hoursMatch = str.match(/(\d+)\s*hours?/);
    const minutesMatch = str.match(/(\d+)\s*minutes?/);
    return {
      hours: hoursMatch ? parseInt(hoursMatch[1], 10) : 0,
      minutes: minutesMatch ? parseInt(minutesMatch[1], 10) : 0
    };
  }

  private parseValueUnit(str: string): { value: number, unit: string } {
    const match = str.match(/(\d+)\s*(\w+)/);
    return {
      value: match ? parseInt(match[1], 10) : 0,
      unit: match ? match[2] : 'minutes'
    };
  }

  async loadProjectWithExtras(projectId: string) {
    this.projectId = projectId;
    await this.loadProjectData(projectId);
    await this.getTimekitProjects();
  }

  async getTimekitProjects() {
    this.loading = true;
    try {
      const response = await this.lambdaService.getTimekitProjects(this.themingService.shopId) as { data: Project[] };
      this.projects = response.data || [];

      if (this.isEditMode && this.projects.length > 0 && this.projectId) {
        const project = this.projects.find(p =>
          p.projectId === this.projectId || p.externalId === this.projectId
        );

        if (project) {
          this.projectForm.patchValue({
            resource: project.timekitResourceId || '',
            projectType: project.projectType ?? 'private',
            selectedCategory: project.categoryId || '',
            selectedSubCategory: project.subCategoryId || '',
            selectedSubSubCategory: project.subSubCategoryId || ''
          });

          this.selectedOption = this.getSelectedOptionFromProject(project);

          if (!this.dbProjectLoaded) {
            // Patch solo si no vino desde la DB
            this.projectForm.patchValue({
              resource: project.timekitResourceId || '',
              projectType: project.projectType ?? 'private',
              selectedCategory: project.categoryId || '',
              selectedSubCategory: project.subCategoryId || '',
              selectedSubSubCategory: project.subSubCategoryId || ''
            });

            this.selectedOption = this.getSelectedOptionFromProject(project);

            if (project.subSubCategoryId) this.projectForm.patchValue({ categoryOption: 'subSubcategories' });
            else if (project.subCategoryId) this.projectForm.patchValue({ categoryOption: 'subCategories' });
            else if (project.categoryId) this.projectForm.patchValue({ categoryOption: 'categories' });
          }

          // Preselect correct categoryOption based on what's available
          if (project.subSubCategoryId) this.projectForm.patchValue({ categoryOption: 'subSubcategories' });
          else if (project.subCategoryId) this.projectForm.patchValue({ categoryOption: 'subCategories' });
          else if (project.categoryId) this.projectForm.patchValue({ categoryOption: 'categories' });
        } else {
          console.warn('Project not found in getTimekitProjects() for ID or ExternalId:', this.projectId);
        }
      }
    } catch (error) {
      console.error('Error al obtener proyectos:', error);
      this.projects = [];
    } finally {
      this.loading = false;
    }
  }

  loadProjectData(projectId: string): Promise<void> {
    return this.apiService.getTimekitProjectById(this.themingService.shopId, projectId).then((res: any) => {
      const projects = JSON.parse(res.body)?.data;
      if (!projects || projects.length === 0) {
        console.error("No se encontró el proyecto.");
        return;
      }

      const project = projects[0];

      this.projectForm.patchValue({
        projectId: project.id,
        name: project.name,
        slug: project.slug,
        allow_conference: project.allow_conference,
        resource: project.resources?.[0],
        availability: {
          ...project.availability,
          length: this.parseTimeStringToObject(project.availability.length),
          buffer: this.parseValueUnit(project.availability.buffer),
          from: this.parseValueUnit(project.availability.from),
        },
        booking: project.booking
      });

      const timeConstraints = project.availability_constraints?.filter((c: any) => c.allow_day_and_time) || [];
      const blockConstraints = project.availability_constraints?.filter((c: any) => c.block_hours) || [];

      this.setCustomerFields(project.customer_fields);
      this.setAvailabilityConstraints(timeConstraints);
      this.setBlockHours(blockConstraints);
    });
  }

  async loadCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);
      const categoryRows = Array.isArray(response[0]) ? response[0] : [];
      this.categories = categoryRows;
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }
  
  
  async loadSubCategories(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getSubCategories(this.themingService.shopId);
      this.subCategories = Array.isArray(response.subCategories) ? response.subCategories : [];
    } catch (error) {
      console.error('Error fetching subCategories:', error);
    } finally {
      this.loading = false;
    }
  }

  async loadSubSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubSubCategories(this.themingService.shopId);
      const subSubCategoryRows = Array.isArray(response.subSubCategories) ? response.subSubCategories : [];
      this.subSubCategories = subSubCategoryRows;
    } catch (error) {
      console.error('Error fetching subSubCategories:', error);
    }
  }

  onOptionChange(option: string) {
    this.selectedOption = option;
    this.resetSelections();
  }

  resetSelections() {
    this.selectedCategoryId = 0;
    this.selectedSubCategoryId = 0;
    this.selectedSubSubCategoryId = 0;
  }

  onCategoryChange(event: any) {
    this.selectedCategoryId = +event.target.value;
  }

  onSubCategoryChange(event: any) {
    this.selectedSubCategoryId = +event.target.value;
  }

  onSubSubCategoryChange(event: any) {
    this.selectedSubSubCategoryId = +event.target.value;
  }

  private formatAvailabilityFormValue(value: any) {
    return {
      mode: value?.mode || 'roundrobin_random',
      length: `${value?.length?.hours || 0} hours ${value?.length?.minutes || 0} minutes`,
      from: `${value?.from?.value || 0} ${value?.from?.unit || 'hours'}`,
      to: `${value?.availability?.to || 1} months`,
      buffer: `${value?.buffer?.value || 0} ${value?.buffer?.unit || 'minutes'}`,
      ignore_all_day_events: value?.ignore_all_day_events ?? true
    };
  }

  async getTimekitResources() {
    this.loading = true;
    this.showNoResourcesMessage = false;

    try {
      const response = await this.lambdaService.getTimekitResources(this.themingService.shopId) as { data: Array<{ timekitResourceId: number, resourceId: string, resourceName: string | null }> };

      this.resources = response.data || [];

      if (this.resources.length === 0) {
        this.showNoResourcesMessage = true;
      } else {
        const resourceIds = this.resources.map(resource => resource.resourceId);
        const resourceNames = this.resources.map(resource => resource.resourceName);
        const timekitResourceIds = this.resources.map(resource => resource.timekitResourceId);

        this.projectForm.get('resources')?.setValue(resourceIds);
        this.projectForm.get('resourceNames')?.setValue(resourceNames);
        this.projectForm.get('timekitResources')?.setValue(timekitResourceIds);
      }
    } catch (error) {
      this.resources = [];
    } finally {
      this.loading = false;
    }
  }

  changeResource(event: Event) {
    const element = event.target as HTMLSelectElement;
    const selectedResourceId = element.value;

    this.projectForm.get('resource')?.setValue(selectedResourceId);

    const selectedResource = this.resources.find(resource => resource.resourceId === selectedResourceId);

    if (selectedResource) {
      this.timekitResourceId = selectedResource.timekitResourceId;
    }
  }

  generateExternalId(): string {
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1_000_000).toString().padStart(6, '0');
    return `ext-${timestamp}-${randomPart}`;
  }

  generateSlug(): string {
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1_000_000).toString().padStart(6, '0');
    const cleanedShopName = this.shopName?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'default';
    return `${cleanedShopName}-${timestamp}-${randomPart}`;
  }

  get availabilityConstraints() {
    return this.projectForm.get('availability_constraints') as FormArray;
  }

  public addAvailabilityConstraint(): void {
    const constraintsArray = this.projectForm.get('availability_constraints') as FormArray;
    constraintsArray.push(
      this.fb.group({
        allow_day_and_time: this.fb.group({
          day: ['', Validators.required],
          start: ['07:00', Validators.required],
          end: ['18:00', Validators.required]
        })
      })
    );
  }

  removeAvailabilityConstraint(index: number): void {
    (this.projectForm.get('availability_constraints') as FormArray).removeAt(index);
  }

  public addBlockHours(): void {
    const blockHoursArray = this.projectForm.get('block_hours') as FormArray;
    blockHoursArray.push(
      this.fb.group({
        start: ['', Validators.required],
        end: ['', Validators.required]
      })
    );
  }

  get blockHoursControls() {
    return (this.projectForm.get('block_hours') as FormArray).controls;
  }

  public removeBlockHours(index: number): void {
    (this.projectForm.get('block_hours') as FormArray).removeAt(index);
  }

  setListingProjects() {
    this.settingsService.setListingProjects();
  }

  getDayInEnglish(index: number): string {
    return this.daysOfWeek[index].toLowerCase();
  }

  get customerFields(): FormArray {
    return this.projectForm.get('customer_fields') as FormArray;
  }

  removeCustomerField(index: number): void {
    this.customerFields.removeAt(index);
  }

  createProject(): void {
    if (this.projectForm.valid) {
      this.loading = true;
      const externalId = this.generateExternalId();

      const {
        name,
        slug,
        allow_conference,
        resources,
        availability,
        availability_constraints,
        booking,
        customer_fields
      } = this.projectForm.value;

      const shopId = this.themingService.shopId || 0;

      const customerFieldsArray = this.projectForm.value.customer_fields;
      const customerFieldsObject = customerFieldsArray.reduce((acc: any, field: any) => {
        acc[field.title.toLowerCase()] = field;
        return acc;
      }, {});

      if (!customerFieldsObject["name"]) {
        customerFieldsObject["name"] = {
          title: "name",
          prefilled: "",
          readonly: false
        };
      }

      if (!customerFieldsObject["email"]) {
        customerFieldsObject["email"] = {
          title: "email",
          prefilled: "",
          readonly: false
        };
      }

      const availabilityConstraints = this.projectForm.value.availability_constraints
        .filter((ac: any) => ac.allow_day_and_time?.day && ac.allow_day_and_time?.start && ac.allow_day_and_time?.end)
        .map((ac: any) => ({ allow_day_and_time: ac.allow_day_and_time }));

      const blockHoursArray = this.projectForm.value.block_hours || [];
      const validBlockHours = blockHoursArray
        .filter((bh: { start: string, end: string }) => bh.start && bh.end)
        .map((bh: { start: string, end: string }) => ({ block_hours: bh }));

      if (validBlockHours.length > 0) {
        availabilityConstraints.push(...validBlockHours);
      }

      const payload = {
        action: 'projects',
        shopId,
        body: {
          name,
          slug: this.generateSlug(),
          allow_conference,
          resources: [this.projectForm.get('resource')?.value],
          availability: this.formatAvailabilityFormValue(availability),
          availability_constraints: availabilityConstraints,
          booking: {
            graph: booking.graph || "instant",
            what: booking.what,
            where: booking.where,
            description: booking.description || "Default description"
          },
          customer_fields: customerFieldsObject,
          meta: {
            external_id: externalId
          }
        }
      };

      const resourceId = resources && resources.length > 0 ? resources[0] : 'Resource ID no encontrado';
      const where = this.projectForm.get('booking.where')?.value;

  this.apiService.createProject(payload)
    .then((data: any) => {
      this.loading = false;
      let parsedBody: any;
      if (data?.body) {
        try {
          parsedBody = JSON.parse(data.body);
        } catch (e) {
          console.error("Error al parsear 'body' de la respuesta:", e);
          return;
        }
      } else {
        parsedBody = data; // ✅ Usamos data directo si ya es objeto
      }

      // 👇 Validación con el campo que sí existe
      if (parsedBody?.message === "Project created successfully in Timekit") {
        const projectName = parsedBody.project?.data?.name || 'Nombre no encontrado';
        const projectId = parsedBody.project?.data?.id || 'ID no encontrado';

        this.createTimekitProjects(
          projectName,
          projectId,
          resourceId,
          where,
          externalId,
          this.projectForm.value.projectType
        );

        $('#successProjectModal').modal('show');
        $('#successProjectModal').on('hidden.bs.modal', () => {
          this.projectForm.reset();
          this.settingsService.setListingProjects();
        });
      } else {
        console.error("❌ Respuesta inesperada:", parsedBody);
        this.showErrorModal();
      }
    })
    .catch((error) => {
      this.loading = false;
      const errorMessage = error?.response?.message || 'Ocurrió un error al crear el proyecto';
      this.showErrorModal();
      console.error('Error en creación de proyecto:', errorMessage);
    });

    } else {
      this.showRequiredFieldsModal();
    }
  }

  updateProject(): void {
    const shopId = this.themingService.shopId;
    const projectId = this.projectForm.value.projectId;

    if (!projectId) {
      console.error("Error: projectId no está definido.");
      return;
    }

    const availabilityConstraints = this.formatAvailabilityConstraints(
      this.projectForm.value.availability_constraints,
      this.projectForm.value.block_hours
    );

    const customerFields = this.formatCustomerFields(this.projectForm.value.customer_fields);

    const body = {
      action: 'updateProject',
      name: this.projectForm.value.name,
      slug: this.projectForm.value.slug,
      allow_conference: +this.projectForm.value.allow_conference,
      resource: this.projectForm.value.resource,
      availability: this.formatAvailabilityFormValue(this.projectForm.value.availability),
      booking: {
        graph: this.projectForm.value.booking?.graph || 'instant',
        what: this.projectForm.value.booking?.what || '',
        where: this.projectForm.value.booking?.where || '',
        description: this.projectForm.value.booking?.description || ''
      },
      availability_constraints: availabilityConstraints,
      customer_fields: customerFields
    };

    const formValue = this.projectForm.getRawValue();
    let categoryId = 0;

    switch (formValue.categoryOption) {
      case 'categories':
        categoryId = +formValue.selectedCategory;
        break;
      case 'subCategories':
        categoryId = +formValue.selectedSubCategory;
        break;
      case 'subSubcategories':
        categoryId = +formValue.selectedSubSubCategory;
        break;
    }

    const completePayload = {
      shopId,
      projectId,
      categoryId,
      projectType: formValue.projectType,
      ...body
    };

    try {
      const payload = completePayload;
      this.apiService.updateProject(shopId, projectId, this.selectedCategoryId, formValue.projectType, payload)
        .then(() => {
          $('#successEditProjectModal').modal('show');
          $('#successEditProjectModal').on('hidden.bs.modal', () => {
            this.projectForm.reset();
            this.settingsService.setListingProjects();
          });
        })
        .catch(err => {
          console.error('Error al actualizar proyecto:', err);
          this.showErrorModal();
        });
    } catch (e) {
      console.error('Error generando JSON válido:', e);
      this.showErrorModal();
    }
  }

  validateFormData(): void {
    if (this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();

      if (
        this.projectForm.get('projectType')?.value === 'private' &&
        (!this.selectedOption ||
          (this.selectedOption === 'categories' && !this.selectedCategoryId) ||
          (this.selectedOption === 'subCategories' && !this.selectedSubCategoryId) ||
          (this.selectedOption === 'subSubcategories' && !this.selectedSubSubCategoryId))
      ) {
        $('#requiredProjectModal').modal('show');
      }

      return;
    }

    if (this.isEditMode) {
      this.updateProject();
    } else {
      this.createProject();
    }
  }

  showErrorModal(): void {
    $('#errorResourceModal').modal('show');
    $('#errorResourceModal').on('hidden.bs.modal', () => {
    });
    $('#errorProjectModal').modal('show');
    $('#errorProjectModal').on('hidden.bs.modal', () => {
    });
  }

  showRequiredFieldsModal(): void {
    $('#requiredProjectModal').modal('show');
  }

  closeModal() {
    $('#requiredProjectModal').modal('hide');
    $('#errorProjectModal').modal('hide');
  }

  closeSuccessModal() {
    $('#successProjectModal').modal('hide');
    $('#successEditProjectModal').modal('hide');

    setTimeout(() => {
      this.setListingProjects();

      const tabsComponent = (window as any).timekitTabs as TimekitTabsComponent;
      if (tabsComponent?.setActiveTab) {
        const index = tabsComponent['tabs'].findIndex(t => t.component === TimekitOptionsComponent);
        tabsComponent.setActiveTab(index);
      }
    }, 500);
  }

  createTimekitProjects(projectName: string, projectId: string, timekitResourceId: number, where: string, externalId: string, projectType: string): void {
    if (!timekitResourceId) return;

    this.apiService
      .createTimekitProjects(
        this.themingService.shopId,
        this.timekitResourceId,
        projectName,
        projectId,
        this.selectedCategoryId,
        this.selectedSubCategoryId,
        this.selectedSubSubCategoryId,
        where,
        externalId,
        projectType
      )
      .then((data: any) => {
        if (!(data.status === 200 && data.msg === 'success')) {
          this.showErrorModal();
        }
      })
      .catch(() => {
        this.loading = false;
        this.showErrorModal();
      });
  }
}


